import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Minus, Plus, Moon, Sun } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useSite } from "@/lib/SiteContext";
import { api } from "@/lib/api";
import { ShareButtons } from "@/components/Forms";
import NotFound from "./NotFound";

export default function Chronicles() {
  const { flags } = useSite();
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/public/excerpts").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (flags.excerpts === 0 && items !== null && items.length === 0) return <NotFound />;

  return (
    <div className="bg-ink min-h-screen" data-testid="chronicles-page">
      <Seo title="Inside the Chronicles — Dirty Church Chronicles"
        description="Read approved excerpts from Dirty Church Chronicles: Volume I by Jermaine Miller."
        path="/chronicles" />
      <section className="pt-40 pb-24 max-w-5xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">The Reading Room</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-4">
            Inside the Chronicles
          </h1>
          <p className="font-reading text-xl text-paper/70 max-w-2xl mb-16">
            Approved excerpts from Volume I, published here with the author's permission.
          </p>
        </Reveal>
        <div className="divide-y divide-paper/10 border-y border-paper/10">
          {(items || []).map((ex, i) => (
            <Reveal key={ex.excerpt_id} delay={i * 0.06}>
              <Link to={`/chronicles/${ex.excerpt_id}`} data-testid={`chronicle-item-${ex.excerpt_id}`}
                className="group flex items-baseline justify-between gap-6 py-8 transition-colors duration-300">
                <div className="flex items-baseline gap-8">
                  {ex.roman && (
                    <span className="font-display text-crimson text-xl w-14 shrink-0">{ex.roman}</span>
                  )}
                  <h2 className="font-display text-2xl sm:text-3xl text-paper group-hover:text-crimsonhi transition-colors duration-300">
                    {ex.title}
                  </h2>
                </div>
                <span className="font-ui text-xs text-paper/40 uppercase tracking-[0.2em] shrink-0">
                  {ex.reading_time} min
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}

const SIZES = [
  { key: "s", label: "Small", cls: "text-lg" },
  { key: "m", label: "Medium", cls: "text-xl" },
  { key: "l", label: "Large", cls: "text-2xl" },
];

export function ChronicleReader() {
  const { id } = useParams();
  const [ex, setEx] = useState(null);
  const [missing, setMissing] = useState(false);
  const [list, setList] = useState([]);
  const [size, setSize] = useState("m");
  const [theme, setTheme] = useState("paper");

  useEffect(() => {
    setMissing(false);
    setEx(null);
    api.get(`/public/excerpts/${id}`).then((r) => setEx(r.data)).catch(() => setMissing(true));
    api.get("/public/excerpts").then((r) => setList(r.data)).catch(() => {});
  }, [id]);

  if (missing) return <NotFound />;
  if (!ex) {
    return (
      <div className="bg-ink min-h-screen pt-40 px-6" data-testid="chronicle-loading">
        <p className="font-ui text-paper/40 text-sm uppercase tracking-[0.3em] text-center">Opening chronicle…</p>
      </div>
    );
  }

  const idx = list.findIndex((x) => x.excerpt_id === id);
  const prev = idx > 0 ? list[idx - 1] : null;
  const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;
  const sizeCls = SIZES.find((s) => s.key === size)?.cls || "text-xl";
  const paragraphs = (ex.body || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <div className={`min-h-screen transition-colors duration-500 ${theme === "paper" ? "reader-paper" : "reader-obsidian"}`} data-testid="chronicle-reader">
      <Seo title={`${ex.title} — Dirty Church Chronicles`}
        description={`An approved excerpt from Dirty Church Chronicles: Volume I by Jermaine Miller.`}
        path={`/chronicles/${id}`} />
      <article className="max-w-3xl mx-auto px-6 sm:px-10 pt-36 pb-24">
        <Link to="/chronicles" data-testid="reader-back-link"
          className={`inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] mb-12 transition-opacity duration-300 hover:opacity-70 ${theme === "paper" ? "text-ink/60" : "text-paper/60"}`}>
          <ArrowLeft size={14} /> All chronicles
        </Link>

        <div className={`flex items-center justify-between gap-4 border-b pb-6 mb-12 ${theme === "paper" ? "border-ink/15" : "border-paper/15"}`}>
          <div className="flex items-center gap-2" role="group" aria-label="Text size">
            <button onClick={() => setSize(size === "l" ? "m" : "s")} aria-label="Decrease text size" data-testid="text-size-down"
              className={`p-2 border rounded-full transition-opacity duration-300 hover:opacity-70 ${theme === "paper" ? "border-ink/25" : "border-paper/25"}`}>
              <Minus size={14} />
            </button>
            <button onClick={() => setSize(size === "s" ? "m" : "l")} aria-label="Increase text size" data-testid="text-size-up"
              className={`p-2 border rounded-full transition-opacity duration-300 hover:opacity-70 ${theme === "paper" ? "border-ink/25" : "border-paper/25"}`}>
              <Plus size={14} />
            </button>
          </div>
          <span className={`font-ui text-xs uppercase tracking-[0.2em] ${theme === "paper" ? "text-ink/50" : "text-paper/50"}`}>
            {ex.reading_time} min read
          </span>
          <button onClick={() => setTheme(theme === "paper" ? "obsidian" : "paper")}
            aria-label={theme === "paper" ? "Switch to dark reading theme" : "Switch to paper reading theme"}
            data-testid="theme-toggle-btn"
            className={`p-2 border rounded-full transition-opacity duration-300 hover:opacity-70 ${theme === "paper" ? "border-ink/25" : "border-paper/25"}`}>
            {theme === "paper" ? <Moon size={14} /> : <Sun size={14} />}
          </button>
        </div>

        {ex.roman && (
          <p className="font-display text-crimson text-xl tracking-[0.25em] mb-4" data-testid="reader-roman">{ex.roman}</p>
        )}
        <h1 className={`font-display font-bold text-3xl sm:text-5xl leading-tight mb-12 ${theme === "paper" ? "text-ink" : "text-paper"}`} data-testid="reader-title">
          {ex.title}
        </h1>
        <div className={`font-reading ${sizeCls} leading-relaxed space-y-7 ${theme === "paper" ? "text-ink/85" : "text-paper/85"}`} data-testid="reader-body">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className={`mt-16 pt-8 border-t flex flex-wrap items-center justify-between gap-6 ${theme === "paper" ? "border-ink/15 text-ink/70" : "border-paper/15 text-paper/70"}`}>
          <ShareButtons title={ex.title} text={`An approved excerpt from Dirty Church Chronicles: Volume I`} path={`/chronicles/${id}`} />
          <div className="flex gap-6">
            {prev && (
              <Link to={`/chronicles/${prev.excerpt_id}`} data-testid="reader-prev-link"
                className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] hover:opacity-70 transition-opacity duration-300">
                <ArrowLeft size={13} /> {prev.title}
              </Link>
            )}
            {next && (
              <Link to={`/chronicles/${next.excerpt_id}`} data-testid="reader-next-link"
                className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] hover:opacity-70 transition-opacity duration-300">
                {next.title} <ArrowRight size={13} />
              </Link>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}
